import { Pause, Play, SkipBack, SkipForward, AudioLines } from "lucide-react";

const RATES = [0.5, 1, 1.5, 2];

export function PlayerBar({
  title,
  playing,
  onPlayPause,
  onSkip,
  index,
  total,
  onSeek,
  rate,
  onRateChange,
  voices,
  voiceURI,
  onVoiceChange,
}: {
  title: string;
  playing: boolean;
  onPlayPause: () => void;
  onSkip: (delta: number) => void;
  index: number;
  total: number;
  onSeek: (index: number) => void;
  rate: number;
  onRateChange: (rate: number) => void;
  voices: SpeechSynthesisVoice[];
  voiceURI: string | null;
  onVoiceChange: (uri: string) => void;
}) {
  const preferred = voices.filter((voice) =>
    voice.lang
      ?.toLowerCase()
      .startsWith(navigator.language?.split("-")[0]?.toLowerCase() ?? "en"),
  );
  const list = preferred.length ? preferred : voices;
  const percent = total > 0 ? Math.round(((index + 1) / total) * 100) : 0;

  return (
    <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-4">
      <div className="glass-panel mx-auto flex max-w-5xl flex-col gap-3 rounded-2xl px-5 py-4 shadow-2xl shadow-black/40">
        <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{title}</div>
            <div className="text-xs text-muted-foreground">
              Sentence {Math.min(index + 1, total)} of {total} · {percent}%
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onSkip(-3)}
              aria-label="Skip back"
              className="grid h-9 w-9 place-items-center rounded-lg hover:bg-white/10"
            >
              <SkipBack className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={onPlayPause}
              aria-label={playing ? "Pause" : "Play"}
              className="grid h-11 w-11 place-items-center rounded-full bg-gradient-to-br from-primary to-accent text-primary-foreground hover:opacity-90"
            >
              {playing ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5 translate-x-0.5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => onSkip(3)}
              aria-label="Skip forward"
              className="grid h-9 w-9 place-items-center rounded-lg hover:bg-white/10"
            >
              <SkipForward className="h-5 w-5" />
            </button>
          </div>
          <div className="hidden items-center gap-2 sm:flex">
            <AudioLines className="h-4 w-4 text-muted-foreground" />
            <select
              className="h-9 w-[78px] px-2 text-sm"
              value={String(rate)}
              onChange={(event) => onRateChange(Number(event.target.value))}
              aria-label="Playback rate"
            >
              {RATES.map((value) => (
                <option key={value} value={String(value)}>
                  {value}x
                </option>
              ))}
            </select>
            <select
              className="h-9 w-[180px] px-2 text-sm"
              value={voiceURI ?? ""}
              onChange={(event) => onVoiceChange(event.target.value)}
              aria-label="Voice"
            >
              <option value="" disabled>
                Voice
              </option>
              {list.map((voice) => (
                <option key={voice.voiceURI} value={voice.voiceURI}>
                  {voice.name} ({voice.lang})
                </option>
              ))}
            </select>
          </div>
        </div>
        <input
          type="range"
          min={0}
          max={Math.max(total - 1, 0)}
          step={1}
          value={total > 0 ? index : 0}
          onChange={(event) => onSeek(Number(event.target.value))}
          aria-label="Playback position"
          className="w-full"
        />
      </div>
    </div>
  );
}
